/**
 * Pure half of the e-mail + code login (wave 9, contract #2.5/#2.6, SR v2.1 §3.3/§5.1).
 *
 * Framework-free on purpose — no `fetch`, no `import.meta.env`, no React — the way
 * `./exports-model` and `./envelope` are: the `unit` Playwright project imports this
 * file in plain Node, where `./client`'s `import.meta.env` does not exist. The
 * transport (`./email-code`) owns the wire, this file owns every DECISION:
 *
 *   - the address shape gate (the only client-side validation of #2.5),
 *   - the code shape gate (a non-6-digit code is a 400, never a 401 — do not spend
 *     an attempt on input that cannot exist),
 *   - the countdown, drawn from the cooldown the server ALWAYS ships
 *     (`resendAfterSeconds` on the 200, `Retry-After`/`meta.retryAfterSeconds` on the 429),
 *   - the honest notice when that wait is the per-address BUDGET and not the
 *     routine resend cooldown (Z1 — «Лимит попыток исчерпан…»),
 *   - the error copy, which never hints whether an address has an account (A1):
 *     every failure of #2.6 is one sentence.
 */

/** Body of #2.5. */
export interface EmailCodeRequestInput {
  email: string;
}

/** Body of #2.6. */
export interface EmailCodeVerifyInput {
  email: string;
  code: string;
}

/** The cooldown, wherever the server put it. */
export interface CooldownSeconds {
  resendAfterSeconds: number;
}

/** Paths of the two public hops, relative to the `/api` prefix. */
export const EMAIL_CODE_REQUEST_PATH = '/auth/email-code/request';
export const EMAIL_CODE_VERIFY_PATH = '/auth/email-code/verify';

/**
 * The address shape the api accepts (`format: email`, ≤ 254 — contract #2.5). A
 * deliberately modest check: the SPA only refuses what the api would refuse as a
 * 400, so a curator is never told «такого адреса нет» when the truth is «опечатка».
 */
export function validateEmail(raw: string): string | null {
  const email = raw.trim();
  if (email === '') return 'Введите e-mail.';
  if (email.length > 254) return 'Адрес слишком длинный (максимум 254 символа).';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Введите адрес целиком — например, name@example.ru.';
  return null;
}

/** The code alphabet is six digits; anything else is a shape error, not a wrong code. */
export function isCodeShape(raw: string): boolean {
  return /^\d{6}$/.test(raw.trim());
}

export const CODE_SHAPE_ERROR = 'Код состоит из 6 цифр.';

/** Length of the one-time code — and the point at which the form signs in by itself. */
export const OTP_LENGTH = 6;

/**
 * The digit-only gate of the code field (T.Y18): everything that is not a digit is
 * stripped (a paste from a letter with «Код: 123-456» still lands), and the field
 * never holds more than {@link OTP_LENGTH} characters — a sixth keystroke cannot
 * overflow into a 400 `VALIDATION`.
 */
export function normalizeOtpInput(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, OTP_LENGTH);
}

/** The sixth digit submits: an explicit click stays possible, but is no longer needed. */
export function shouldAutoSubmitOtp(value: string): boolean {
  return value.length === OTP_LENGTH && /^\d{6}$/.test(value);
}

/**
 * The cooldown out of a #2.5 answer. The server ships it on the 200 for EVERY
 * syntactically valid address (even the suppressed send — the answer does not
 * discriminate), and mirrors it as `meta.retryAfterSeconds` / the `Retry-After`
 * header on the 429, so the countdown is always drawable (SR §5.1). Both the raw
 * envelope (`{data:{…}}`) and the unwrapped payload parse.
 */
export function cooldownFromResponse(body: unknown): CooldownSeconds | null {
  if (typeof body !== 'object' || body === null) return null;
  const record = body as Record<string, unknown>;
  const direct = readSeconds(record.resendAfterSeconds);
  if (direct !== null) return { resendAfterSeconds: direct };

  const nested = typeof record.data === 'object' && record.data !== null ? (record.data as Record<string, unknown>) : undefined;
  const fromData = readSeconds(nested?.resendAfterSeconds);
  return fromData === null ? null : { resendAfterSeconds: fromData };
}

/**
 * The 429's cooldown: the structured `meta` first, the honest HTTP header second.
 * Either source is enough to draw the countdown without a second request.
 */
export function cooldownFromRejection(meta: unknown, retryAfterHeader: string | null | undefined): CooldownSeconds | null {
  const fromMeta = readSeconds(
    typeof meta === 'object' && meta !== null ? (meta as Record<string, unknown>).retryAfterSeconds : undefined,
  );
  if (fromMeta !== null) return { resendAfterSeconds: fromMeta };

  const header = Number.parseInt(String(retryAfterHeader ?? '').trim(), 10);
  if (Number.isFinite(header) && header > 0) return { resendAfterSeconds: header };
  return null;
}

function readSeconds(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return null;
  return Math.round(value);
}

/**
 * The countdown label, RU, `М:СС`. `null` when the wait is over — the «Отправить код
 * ещё раз» link turns on exactly then.
 *
 * Above {@link LONG_WAIT_SECONDS} the seconds stop mattering and the label drops
 * to whole minutes (Z1): «через 59:59» ticking away for an hour read as a broken
 * endless countdown, while «через 59 мин» says the same thing honestly.
 */
export const LONG_WAIT_SECONDS = 600;

export function countdownLabel(secondsLeft: number): string | null {
  if (!Number.isFinite(secondsLeft) || secondsLeft <= 0) return null;
  const whole = Math.ceil(secondsLeft);
  const minutes = Math.floor(whole / 60);
  const seconds = whole % 60;
  if (whole >= LONG_WAIT_SECONDS) return `Повторная отправка через ${minutes} мин`;
  return minutes > 0
    ? `Повторная отправка через ${minutes}:${String(seconds).padStart(2, '0')}`
    : `Повторная отправка через ${seconds} с`;
}

/**
 * The honest notice for a LONG wait (Z1): the per-address budget of #2.5 is
 * 3 codes/hour, so the answer that fills the last slot now ships the moment the
 * oldest of them falls out of the window, and the 429 carries the same number as
 * `Retry-After`. Either way the screen says so in words — a silent hour-long
 * countdown was the reason the «Отправить код ещё раз» button read as dead.
 *
 * `null` for a wait that is just the routine resend cooldown — there the plain
 * countdown is all the truth there is.
 */
export const BUDGET_NOTICE_THRESHOLD_SECONDS = 90;

export function budgetLimitCopy(waitSeconds: number | null | undefined): string | null {
  if (typeof waitSeconds !== 'number' || !Number.isFinite(waitSeconds) || waitSeconds <= BUDGET_NOTICE_THRESHOLD_SECONDS) {
    return null;
  }
  const minutes = Math.max(1, Math.ceil(waitSeconds / 60));
  return `Лимит попыток исчерпан. Повторная отправка будет доступна через ${minutes} мин.`;
}

/** Do two typed addresses name the same mailbox? The api's normalization (trim + lower case). */
export function isSameAddress(left: string, right: string): boolean {
  const a = left.trim().toLowerCase();
  const b = right.trim().toLowerCase();
  return a !== '' && a === b;
}

/** The next tick of the countdown — one whole second less, floored at 0. */
export function nextCountdown(secondsLeft: number): number {
  if (!Number.isFinite(secondsLeft)) return 0;
  return Math.max(0, Math.ceil(secondsLeft) - 1);
}

/**
 * What a failed hop says. THREE sentences for #2.5, ONE for #2.6 — the point of
 * A1 is that «нет такого куратора» and «неверный код» are the same sentence, so a
 * stranger cannot walk the address space through the login screen.
 */
export function emailCodeErrorCopy(code: string | undefined, status: number): string {
  if (code === 'EMAIL_CODE_NOT_CONFIGURED' || (status === 503 && code === undefined)) {
    return 'Вход по коду временно недоступен. Войдите через Яндекс или попробуйте позже.';
  }
  if (code === 'TOO_MANY_REQUESTS' || status === 429) {
    return 'Слишком много попыток. Подождите немного и попробуйте снова.';
  }
  if (code === 'EMAIL_CODE_INVALID' || status === 401) {
    return 'Неверный или истёкший код. Запросите новый.';
  }
  if (code === 'VALIDATION' || status === 400) {
    return 'Проверьте правильность заполнения полей.';
  }
  return 'Не удалось выполнить вход. Попробуйте ещё раз позже.';
}

/**
 * Where the SPA goes after a successful #2.6 is NO LONGER THIS FILE'S decision.
 * Until T.Z85 the answer was a hardcoded `/cabinet` made by PROVIDER («a code
 * session is the cabinet-only session», SR §3.4/A3 — the wave-9 rule D21
 * retired), which sent a staff member signing in by code to a cabinet the api
 * refuses, and left the Yandex door sending a КУРАТОР to the staff dashboard's
 * 403. The decision moved to the session: `landingAfterLogin` in
 * `../auth/session-provider` (the Z60 predicate), applied by
 * `landingAfterVerify` in `./email-code` — which reads the role off the
 * `/auth/me` answer the session now owns — and by the `/` index route for the
 * hand-typed/bookmarked address and the OAuth callback's `/`.
 */
